import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Callout, Button, Icon } from '../ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            padding: 32,
            margin: '16px auto',
            maxWidth: 600,
            textAlign: 'center',
          }}
        >
          <div style={{ marginBottom: 24 }}>
            <Icon name="alertCircle" size={48} style={{ color: 'var(--err)', marginBottom: 12 }} />
            <h2 style={{ fontWeight: 600, marginBottom: 8 }}>Something went wrong</h2>
            <p className="muted">We encountered an unexpected error. Please try refreshing the page.</p>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Callout tone="warn" style={{ marginBottom: 24, textAlign: 'left' }}>
              <strong style={{ display: 'block', marginBottom: 4 }}>Error Details:</strong>
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>
                {this.state.error.toString()}
              </pre>
            </Callout>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button onClick={this.handleRetry}>
              <Icon name="refresh" size={14} style={{ marginRight: 6 }} />
              Try Again
            </Button>
            <Button variant="ghost" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
